import { makeStyles } from '@mui/styles';
import Badge from 'apollo-react/components/Badge';
import IconButton from 'apollo-react/components/IconButton';
import IconMenuButton from 'apollo-react/components/IconMenuButton';
import Loader from 'apollo-react/components/Loader';
import Menu from 'apollo-react/components/Menu';
import Tooltip from 'apollo-react/components/Tooltip';
import Typography from 'apollo-react/components/Typography';
import Bell from 'apollo-react-icons/Bell';
import Cog from 'apollo-react-icons/Cog';
import Refresh from 'apollo-react-icons/Refresh';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import NotificationItem from './NotificationItem';
import {
  getNewNotifications,
  handleNotificationActions,
} from '../../../actions/NotificationsActions';
import { NOTIFICATION_ACTIONS } from '../../../constants/NotificationConstants';
import { useTranslation } from '../../../custom-hooks/useTranslation';
import {
  getNotifications,
  getUnreadNotificationsCount,
} from '../../../selectors/NotificationsSelectors';
import { getUITA } from '../../../utils/commonUtils';

const useStyles = makeStyles({
  iconButton: {
    borderRadius: 0,
    color: '#fff',
    fontSize: '1rem',
    marginLeft: '-0.75rem',
    padding: '0.5rem 1rem !important',
    height: '3.5rem !important',
    width: 'auto !important',
    '&:hover, &[aria-owns=notificationsmenu-appbar]': {
      backgroundColor: 'rgba(255, 255, 255, 0.24)',
      color: '#fff',
    },
  },
  root: {
    paddingRight: '0.75rem',
  },
  list: {
    maxHeight: 465,
    width: '440px !important',
    padding: '0 !important',
    '& li': {
      padding: '10px 0px',
    },
  },
  notificationActionsContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    paddingTop: 10,
    paddingLeft: 27,
    alignItems: 'center',
    paddingBottom: 10,
  },
  notificationBarIcon: {
    width: 24,
    height: 24,
  },
  noNotificationContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'content',
    alignItems: 'center',
    margin: '75px 0px',
    color: '#595959',
  },
  noNotificationMessage: {
    color: '#595959',
    fontSize: 18,
  },
  noNotificationsBarIcon: {
    width: 50,
    height: 50,
    marginBottom: 12,
  },
  actionButton: {
    marginRight: 10,
  },
  loaderContainer: {
    position: 'relative',
    height: 240,
  },
});

const Notifications = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const translateString = useTranslation();

  const [anchorEl, setAnchorEl] = useState(null);
  const [isLoading, setLoading] = useState(false);

  const notifications = useSelector(getNotifications);
  const unreadNotificationsCount = useSelector(getUnreadNotificationsCount);

  const handleNotificationsOpen = (e) => {
    setAnchorEl(e.currentTarget);
  };

  const handleNotificationsClose = () => {
    setAnchorEl(null);
  };

  const handleRefresh = async () => {
    setLoading(true);
    await dispatch(getNewNotifications());
    setLoading(false);
  };

  const handleRemoveAll = () => {
    const notificationActions = notifications.map((notification) => {
      return { Action: NOTIFICATION_ACTIONS.DELETE, NotificationId: notification.id };
    });

    dispatch(handleNotificationActions(notificationActions));
  };

  const handleMarkAllRead = () => {
    //send to api only those notifications which are not read yet
    const notificationActions = notifications.reduce((acc, notification) => {
      if (notification.action === NOTIFICATION_ACTIONS.UNREAD) {
        return [...acc, { Action: NOTIFICATION_ACTIONS.READ, NotificationId: notification.id }];
      }

      return acc;
    }, []);

    dispatch(handleNotificationActions(notificationActions));
  };

  const notificationsCount = notifications.length;

  const menuItems = [
    {
      text: translateString('lblRemoveAll'),
      onClick: handleRemoveAll,
    },
    {
      text: translateString('Mark all as read'),
      disabled: !unreadNotificationsCount,
      onClick: handleMarkAllRead,
    },
  ];

  const showNotifications = !!(notificationsCount && !isLoading);
  const showEmptyMessage = !notificationsCount && !isLoading;

  return (
    <div className={classes.root} ui-ta={getUITA('Notifications')}>
      <Tooltip title={translateString('Notifications')} placement="bottom">
        <div>
          <Badge badgeContent={unreadNotificationsCount}>
            <IconButton
              aria-haspopup="true"
              aria-owns={anchorEl ? 'notificationsmenu-appbar' : null}
              className={classes.iconButton}
              disableRipple
              onClick={handleNotificationsOpen}
            >
              <Bell className={classes.notificationBarIcon} />
            </IconButton>
          </Badge>
        </div>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{
          horizontal: 'right',
          vertical: 'bottom',
        }}
        classes={{ list: classes.list }}
        style={{ padding: 0 }}
        disableAutoFocusItem
        id="NotificationsListContainer"
        onClose={handleNotificationsClose}
        open={Boolean(anchorEl)}
        PaperProps={{
          square: true,
          style: { marginLeft: -3, borderTopLeftRadius: 0, borderTopRightRadius: 0 },
        }}
      >
        <div className={classes.notificationActionsContainer} role="presentation">
          <Typography variant="h3">{translateString('Notifications')}</Typography>
          <div>
            <IconMenuButton
              menuItems={menuItems}
              id="notificationsActions"
              disabled={!notificationsCount || isLoading}
              aria-label={translateString('Actions')}
            >
              <Cog fontSize="extraSmall" />
            </IconMenuButton>
            <IconButton
              className={classes.actionButton}
              onClick={handleRefresh}
              disabled={isLoading}
              aria-label={translateString('Refresh')}
            >
              <Refresh />
            </IconButton>
          </div>
        </div>
        {showNotifications &&
          notifications.map(({ id, ...otherProps }) => (
            <NotificationItem
              key={id}
              id={id}
              handleNotificationsClose={handleNotificationsClose}
              {...otherProps}
            />
          ))}
        {showEmptyMessage && (
          <div className={classes.noNotificationContainer}>
            <Bell className={classes.noNotificationsBarIcon} />
            <Typography className={classes.noNotificationMessage}>
              {translateString('No notifications')}
            </Typography>
          </div>
        )}
        {isLoading && (
          <div className={classes.loaderContainer}>
            <Loader isInner />
          </div>
        )}
      </Menu>
    </div>
  );
};

export default Notifications;
